import React, { useState, useEffect } from 'react'
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PlusIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline'
import { message } from 'antd'
import { billsAPI, financeiroAPI } from '@/services/api'
import { useAuthStore } from '../../stores/authStore'
import { createBillSchema, paymentSchema, editBillSchema } from '@/schemas/billSchemas'
import { z } from 'zod'
import { API_BASE_URL } from '@/services/api'

interface Bill {
  id: number
  type: string
  status: string
  description: string
  document_number?: string
  issue_date: string
  due_date: string
  payment_date?: string
  original_amount?: string
  valor?: string  // Backend API field
  discount_amount?: string
  interest_amount?: string
  fine_amount?: string
  paid_amount?: string
  final_amount?: string
  balance_amount?: string
  customer_id?: number
  supplier_id?: number
  category_id?: number
  is_installment?: boolean
  installment_number?: number
  total_installments?: number
  payment_method?: string
  notes?: string
  is_overdue?: boolean
  days_overdue?: number
  created_at: string
  updated_at: string
}

interface BillStats {
  total_pending: number
  total_overdue: number
  total_paid_this_month: number
  bills_due_this_week: number
  average_bill_amount: number
  largest_pending_bill: number
  suppliers_count: number
  pending_amount: number
  overdue_amount: number
}

interface Supplier {
  id: number
  nome: string
  documento?: string
  email?: string
  telefone?: string
  endereco?: string
  cidade?: string
  estado?: string
  cep?: string
  status: string
}

const BillsPage: React.FC = () => {
  const { token } = useAuthStore()
  const [bills, setBills] = useState<Bill[]>([])
  const [stats, setStats] = useState<BillStats | null>(null)
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showPayModal, setShowPayModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null)
  const [selectedBills, setSelectedBills] = useState<number[]>([]) // For bulk actions
  const [searchTerm, setSearchTerm] = useState('') // For text search
  const [filterStatus, setFilterStatus] = useState<'all' | 'open' | 'pending' | 'overdue' | 'partial' | 'paid'>('all')

  // Advanced filters
  const [filterDateStart, setFilterDateStart] = useState('')
  const [filterDateEnd, setFilterDateEnd] = useState('')
  const [filterCategory, setFilterCategory] = useState<number | 'all'>('all')
  const [filterSupplier, setFilterSupplier] = useState<number | 'all'>('all')
  const [filterAmountMin, setFilterAmountMin] = useState('')
  const [filterAmountMax, setFilterAmountMax] = useState('')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  // Quick date period filter (ERP-style)
  const [selectedPeriod, setSelectedPeriod] = useState<'current_month' | 'last_3_months' | 'last_6_months' | 'current_year' | 'all'>('current_month')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Sorting
  const [sortField, setSortField] = useState<'description' | 'amount' | 'due_date' | 'status'>('due_date')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [formData, setFormData] = useState({
    type: 'payable',
    document_number: '',
    description: '',
    original_amount: '',
    supplier_id: '',
    category_id: '',
    issue_date: '',
    due_date: '',
    notes: ''
  })

  const [paymentData, setPaymentData] = useState({
    account_id: '',
    payment_method: 'debit_card',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  })

  // Validation errors
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({})

  // Helper function to calculate date ranges for quick filters
  const getDateRange = (period: typeof selectedPeriod) => {
    const today = new Date()
    const year = today.getFullYear()
    const month = today.getMonth() // 0-indexed

    switch(period) {
      case 'current_month':
        const firstDayOfMonth = new Date(year, month, 1)
        const lastDayOfMonth = new Date(year, month + 1, 0)
        return {
          start: firstDayOfMonth.toISOString().split('T')[0],
          end: lastDayOfMonth.toISOString().split('T')[0]
        }

      case 'last_3_months':
        const threeMonthsAgo = new Date(year, month - 2, 1)
        const endOfCurrentMonth = new Date(year, month + 1, 0)
        return {
          start: threeMonthsAgo.toISOString().split('T')[0],
          end: endOfCurrentMonth.toISOString().split('T')[0]
        }

      case 'last_6_months':
        const sixMonthsAgo = new Date(year, month - 5, 1)
        const endOfCurrentMonth2 = new Date(year, month + 1, 0)
        return {
          start: sixMonthsAgo.toISOString().split('T')[0],
          end: endOfCurrentMonth2.toISOString().split('T')[0]
        }

      case 'current_year':
        const firstDayOfYear = new Date(year, 0, 1)
        const lastDayOfYear = new Date(year, 11, 31)
        return {
          start: firstDayOfYear.toISOString().split('T')[0],
          end: lastDayOfYear.toISOString().split('T')[0]
        }

      case 'all':
        return { start: '', end: '' }

      default:
        return { start: '', end: '' }
    }
  }

  // Set default filter to current month on mount
  useEffect(() => {
    const defaultRange = getDateRange('current_month')
    setFilterDateStart(defaultRange.start)
    setFilterDateEnd(defaultRange.end)
  }, [])

  // Handle quick period filter selection
  const handlePeriodChange = (period: typeof selectedPeriod) => {
    setSelectedPeriod(period)
    const dateRange = getDateRange(period)
    setFilterDateStart(dateRange.start)
    setFilterDateEnd(dateRange.end)
    setCurrentPage(1)
  }

  useEffect(() => {
    const fetchBillsData = async () => {
      try {
        setLoading(true)

        // Construir URL com filtros de data
        const params = new URLSearchParams()
        if (filterDateStart) params.append('vencimento_inicio', filterDateStart)
        if (filterDateEnd) params.append('vencimento_fim', filterDateEnd)
        params.append('limit', '500')
        params.append('offset', '0')

        // Buscar contas a pagar com LIMITE FIXO (ERP-style)
        const billsResponse = await fetch(`${API_BASE_URL}/financeiro/contas-pagar?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!billsResponse.ok) {
          throw new Error(`Erro ${billsResponse.status} ao buscar contas a pagar`)
        }

        const rawData = await billsResponse.json()

        // Convert backend conta_pagar format to frontend Bill interface
        const billsData = rawData.map((conta: any) => ({
          id: conta.id,
          type: conta.tipo || 'ACCOUNTS_PAYABLE',
          status: conta.status,
          description: conta.descricao,
          issue_date: conta.created_at,
          due_date: conta.data_vencimento,
          payment_date: conta.data_pagamento,
          original_amount: parseFloat(conta.valor) || 0,
          valor: parseFloat(conta.valor) || 0,
          final_amount: parseFloat(conta.valor) || 0,
          paid_amount: conta.data_pagamento ? parseFloat(conta.valor) || 0 : 0,
          balance_amount: conta.data_pagamento ? 0 : parseFloat(conta.valor) || 0,
          supplier_id: conta.fornecedor?.id,
          category_id: conta.categoria?.id,
          document_number: conta.numero_documento,
          notes: conta.observacoes,
          is_overdue: conta.status === 'overdue',
          days_overdue: 0,
          created_at: conta.created_at,
          updated_at: conta.updated_at
        }))

        console.log(`✅ Carregados ${billsData.length} registros de contas a pagar`)
        setBills(billsData)

        // Buscar estatísticas em paralelo
        const statsData = await billsAPI.getStats()
        setStats(statsData)

        // Buscar fornecedores diretamente do endpoint correto
        const suppliersResponse = await fetch(`${API_BASE_URL}/financeiro/fornecedores`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (suppliersResponse.ok) {
          const suppliersData = await suppliersResponse.json()
          setSuppliers(suppliersData)
        } else {
          console.error('Erro ao buscar fornecedores:', suppliersResponse.statusText)
          setSuppliers([])
        }

        // Buscar contas bancárias
        const accountsResponse = await fetch(`${API_BASE_URL}/accounts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (accountsResponse.ok) {
          const accountsData = await accountsResponse.json()
          setAccounts(Array.isArray(accountsData?.accounts) ? accountsData.accounts : [])
        } else {
          console.error('Erro ao buscar contas bancárias:', accountsResponse.statusText)
          setAccounts([])
        }

        // Buscar categorias
        const categoriesResponse = await fetch(`${API_BASE_URL}/categories`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (categoriesResponse.ok) {
          const categoriesData = await categoriesResponse.json()
          console.log('DEBUG: Categorias carregadas:', categoriesData)
          // O endpoint retorna { categories: [...] }
          const cats = categoriesData.categories || categoriesData
          setCategories(Array.isArray(cats) ? cats : [])
        } else {
          console.error('Erro ao buscar categorias:', categoriesResponse.statusText)
          setCategories([])
        }
        setError(null)
      } catch (err: any) {
        console.error('Bills API error:', err)
        setError(err.message || 'Erro ao carregar dados')
        // On error, set empty state instead of mock data
        setBills([])
        setStats({
          total_pending: 0,
          total_overdue: 0,
          total_paid_this_month: 0,
          bills_due_this_week: 0,
          average_bill_amount: 0,
          largest_pending_bill: 0,
          suppliers_count: 0,
          pending_amount: 0,
          overdue_amount: 0
        })
        setSuppliers([])
      } finally {
        setLoading(false)
      }
    }

    fetchBillsData()
  }, [filterDateStart, filterDateEnd]) // Refetch when date filters change

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const handleCreateBill = async (e: React.FormEvent) => {
    e.preventDefault()

    // Clear previous errors
    setFormErrors({})

    try {
      // Validate with Zod
      const validatedData = createBillSchema.parse(formData)

      // Use first available account or default to 1
      const accountId = accounts.length > 0 ? accounts[0].id : 1

      const billData = {
        descricao: validatedData.description,
        valor: parseFloat(validatedData.original_amount),
        data_vencimento: validatedData.due_date,
        data_competencia: validatedData.issue_date || validatedData.due_date, // Use due_date if issue_date is empty
        numero_documento: validatedData.document_number,
        fornecedor_id: parseInt(validatedData.supplier_id),
        conta_id: accountId,
        categoria_id: validatedData.category_id ? parseInt(validatedData.category_id) : 2,
        observacoes: validatedData.notes || ''
      }

      console.log('DEBUG BillsPage: Dados do formulário sendo enviados:', JSON.stringify(billData, null, 2))
      await financeiroAPI.contasPagar.create(billData)

      // Reset form and close modal
      setFormData({
        type: 'payable',
        document_number: '',
        description: '',
        original_amount: '',
        supplier_id: '',
        category_id: '',
        issue_date: '',
        due_date: '',
        notes: ''
      })
      setFormErrors({})
      setShowCreateModal(false)

      // Show success message
      message.success('Conta a pagar criada com sucesso!')

      // Refresh data
      window.location.reload()

    } catch (err: any) {
      if (err instanceof z.ZodError) {
        // Handle Zod validation errors
        const errors: Record<string, string> = {}
        err.errors.forEach((error) => {
          if (error.path) {
            errors[error.path[0]] = error.message
          }
        })
        setFormErrors(errors)
        message.error('Por favor, corrija os erros no formulário')
        setError('Por favor, corrija os erros no formulário')
      } else {
        message.error(err.message || 'Erro ao criar conta')
        setError(err.message || 'Erro ao criar conta')
      }
    }
  }

  const handlePayBill = (bill: Bill) => {
    setSelectedBill(bill)
    setPaymentData({
      account_id: '',
      payment_method: 'debit_card',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    })
    setShowPayModal(true)
  }

  const handleEditBill = (bill: Bill) => {
    setSelectedBill(bill)
    // Ensure the amount is treated as a regular number, not multiplied by 100
    // Handle different possible data formats from API
    const amount = parseFloat(bill.original_amount || bill.valor || '0')
    const formattedAmount = amount.toString()

    setFormData({
      type: 'payable',
      document_number: bill.document_number || '',
      description: bill.description,
      original_amount: formattedAmount,
      supplier_id: bill.supplier_id?.toString() || '',
      issue_date: bill.issue_date,
      due_date: bill.due_date,
      notes: bill.notes || ''
    })
    setShowEditModal(true)
  }

  const processPayment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedBill) return

    // Clear previous errors
    setPaymentErrors({})

    try {
      // Validate with Zod
      const validatedData = paymentSchema.parse(paymentData)

      const response = await fetch(`${API_BASE_URL}/financeiro/contas-pagar/${selectedBill.id}/pagar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: parseInt(validatedData.account_id),
          payment_method: validatedData.payment_method,
          payment_date: validatedData.payment_date,
          notes: validatedData.notes || ''
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erro ao processar pagamento')
      }

      const result = await response.json()
      console.log('Pagamento processado:', result)

      // Reset modal
      setShowPayModal(false)
      setSelectedBill(null)

      // Show success message
      message.success('Pagamento processado com sucesso!')

      // Refresh data
      window.location.reload()

    } catch (error: any) {
      if (error instanceof z.ZodError) {
        // Handle Zod validation errors
        const errors: Record<string, string> = {}
        error.errors.forEach((err) => {
          if (err.path) {
            errors[err.path[0]] = err.message
          }
        })
        setPaymentErrors(errors)
        message.error('Por favor, corrija os erros no formulário de pagamento')
        setError('Por favor, corrija os erros no formulário de pagamento')
      } else {
        console.error('Erro ao processar pagamento:', error)
        message.error(error.message || 'Erro ao processar pagamento')
        setError(error.message || 'Erro ao processar pagamento')
      }
    }
  }

  const handleBulkMarkAsPaid = async () => {
    if (selectedBills.length === 0) return

    try {
      setLoading(true)
      const today = new Date().toISOString().split('T')[0]

      // Get first account ID from accounts (if available)
      const accountId = accounts.length > 0 ? accounts[0].id : 1

      // Process all selected bills in parallel
      const promises = selectedBills.map(billId =>
        fetch(`${API_BASE_URL}/financeiro/contas-pagar/${billId}/pagar`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            account_id: accountId,
            payment_method: 'pix',
            payment_date: today,
            notes: 'Pagamento em lote'
          })
        })
      )

      const results = await Promise.allSettled(promises)

      // Count successes and failures
      const successful = results.filter(r => r.status === 'fulfilled' && (r.value as Response).ok).length
      const failed = results.length - successful

      if (failed > 0) {
        message.warning(`${successful} conta(s) marcada(s) como paga. ${failed} falharam.`)
        setError(`${successful} conta(s) marcada(s) como paga. ${failed} falharam.`)
      } else {
        message.success(`${successful} conta(s) marcada(s) como paga com sucesso!`)
      }

      // Clear selection and refresh
      setSelectedBills([])

      const billsData = await fetchAllBills()
      const statsData = await billsAPI.getStats()
      setBills(billsData)
      setStats(statsData)

    } catch (error: any) {
      console.error('Erro no pagamento em lote:', error)
      message.error(error.message || 'Erro ao processar pagamentos em lote')
      setError(error.message || 'Erro ao processar pagamentos em lote')
    } finally {
      setLoading(false)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedBills.length === 0) return

    try {
      setLoading(true)

      // Delete all selected bills in parallel
      const promises = selectedBills.map(billId =>
        fetch(`${API_BASE_URL}/financeiro/contas-pagar/${billId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      )

      const results = await Promise.allSettled(promises)

      // Count successes and failures
      const successful = results.filter(r => r.status === 'fulfilled' && (r.value as Response).ok).length
      const failed = results.length - successful

      if (failed > 0) {
        message.warning(`${successful} conta(s) excluída(s). ${failed} falharam.`)
        setError(`${successful} conta(s) excluída(s). ${failed} falharam.`)
      } else {
        message.success(`${successful} conta(s) excluída(s) com sucesso!`)
      }

      // Clear selection and refresh
      setSelectedBills([])

      const billsData = await fetchAllBills()
      const statsData = await billsAPI.getStats()
      setBills(billsData)
      setStats(statsData)

    } catch (error: any) {
      console.error('Erro na exclusão em lote:', error)
      message.error(error.message || 'Erro ao excluir contas em lote')
      setError(error.message || 'Erro ao excluir contas em lote')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateBill = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedBill) return

    try {
      const billData = {
        descricao: formData.description,
        valor: parseFloat(formData.original_amount),
        data_vencimento: formData.due_date,
        fornecedor_id: parseInt(formData.supplier_id),
        conta_id: 1, // Default account - should be replaced with actual account selection
        categoria_id: 2, // Using expense category we created
        observacoes: formData.notes,
        numero_documento: formData.document_number
      }

      const response = await fetch(`${API_BASE_URL}/financeiro/contas-pagar/${selectedBill.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(billData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erro ao atualizar conta')
      }

      // Reset form and close modal
      setFormData({
        type: 'payable',
        document_number: '',
        description: '',
        original_amount: '',
        supplier_id: '',
        category_id: '',
        issue_date: '',
        due_date: '',
        notes: ''
      })
      setShowEditModal(false)
      setSelectedBill(null)

      // Show success message
      message.success('Conta atualizada com sucesso!')

      // Refresh data
      window.location.reload()

    } catch (err: any) {
      message.error(err.message || 'Erro ao atualizar conta')
      setError(err.message || 'Erro ao atualizar conta')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'open': case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'overdue': return 'bg-red-100 text-red-800'
      case 'partial': return 'bg-blue-100 text-blue-800'
      case 'cancelled': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid': return 'Pago'
      case 'open': case 'pending': return 'Pendente'
      case 'overdue': return 'Vencido'
      case 'partial': return 'Parcial'
      case 'cancelled': return 'Cancelado'
      default: return status
    }
  }

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date()
    const due = new Date(dueDate)
    const diffTime = due.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const displayStats = stats || {
    total_pending: 0,
    total_overdue: 0,
    total_paid_this_month: 0,
    bills_due_this_week: 0,
    average_bill_amount: 0,
    largest_pending_bill: 0,
    suppliers_count: 0,
    pending_amount: 0,
    overdue_amount: 0
  }
  const displayBills = Array.isArray(bills) ? bills : []
  const displaySuppliers = Array.isArray(suppliers) ? suppliers : []

  // Handle column sorting
  const handleSort = (field: 'description' | 'amount' | 'due_date' | 'status') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const filteredBills = displayBills.filter(bill => {
    // Filter by status
    const matchesStatus = filterStatus === 'all' || bill.status === filterStatus

    // Filter by search term (description or document_number)
    const matchesSearch = searchTerm === '' ||
      bill.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.document_number?.toLowerCase().includes(searchTerm.toLowerCase())

    // Filter by date range
    const matchesDateStart = !filterDateStart ||
      (bill.due_date && new Date(bill.due_date) >= new Date(filterDateStart))
    const matchesDateEnd = !filterDateEnd ||
      (bill.due_date && new Date(bill.due_date) <= new Date(filterDateEnd))

    // Filter by category
    const matchesCategory = filterCategory === 'all' || bill.category_id === filterCategory

    // Filter by supplier
    const matchesSupplier = filterSupplier === 'all' || bill.supplier_id === filterSupplier

    // Filter by amount range
    const billAmount = parseFloat(bill.original_amount || bill.valor || '0')
    const matchesAmountMin = !filterAmountMin || billAmount >= parseFloat(filterAmountMin)
    const matchesAmountMax = !filterAmountMax || billAmount <= parseFloat(filterAmountMax)

    return matchesStatus && matchesSearch && matchesDateStart && matchesDateEnd &&
           matchesCategory && matchesSupplier && matchesAmountMin && matchesAmountMax
  })

  // Sort filtered bills
  const sortedBills = [...filteredBills].sort((a, b) => {
    let aValue: any, bValue: any

    switch (sortField) {
      case 'description':
        aValue = a.description?.toLowerCase() || ''
        bValue = b.description?.toLowerCase() || ''
        break
      case 'amount':
        aValue = parseFloat(a.original_amount || a.valor || '0')
        bValue = parseFloat(b.original_amount || b.valor || '0')
        break
      case 'due_date':
        aValue = new Date(a.due_date || 0).getTime()
        bValue = new Date(b.due_date || 0).getTime()
        break
      case 'status':
        aValue = a.status || ''
        bValue = b.status || ''
        break
      default:
        return 0
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  // Pagination logic
  const totalPages = Math.ceil(sortedBills.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedBills = sortedBills.slice(startIndex, endIndex)

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterStatus, filterDateStart, filterDateEnd, filterCategory, filterSupplier, filterAmountMin, filterAmountMax])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar contas a pagar</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DocumentTextIcon className="w-7 h-7 text-red-600" />
            Contas a Pagar
          </h1>
          <p className="text-gray-600">Gestão completa de contas e fornecedores</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-red-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-red-700 hover:to-pink-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <PlusIcon className="w-5 h-5" />
          Nova Conta
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Pendentes</p>
              <p className="text-2xl font-bold">{displayStats.total_pending}</p>
              <p className="text-sm">{formatCurrency(displayStats.pending_amount)}</p>
            </div>
            <ClockIcon className="w-8 h-8 text-red-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Vencidas</p>
              <p className="text-2xl font-bold">{displayStats.total_overdue}</p>
              <p className="text-sm">{formatCurrency(displayStats.overdue_amount)}</p>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-orange-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Pagas este Mês</p>
              <p className="text-2xl font-bold">{displayStats.total_paid_this_month}</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Ticket Médio</p>
              <p className="text-2xl font-bold">{formatCurrency(displayStats.average_bill_amount)}</p>
            </div>
            <CurrencyDollarIcon className="w-8 h-8 text-blue-200" />
          </div>
        </div>
      </div>

      {/* Quick Period Filters (ERP-style) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Filtro de Período</h3>
            <p className="text-xs text-gray-500">Selecione um período para visualizar as contas</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handlePeriodChange('current_month')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedPeriod === 'current_month'
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Este Mês
            </button>
            <button
              onClick={() => handlePeriodChange('last_3_months')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedPeriod === 'last_3_months'
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Últimos 3 Meses
            </button>
            <button
              onClick={() => handlePeriodChange('last_6_months')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedPeriod === 'last_6_months'
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Últimos 6 Meses
            </button>
            <button
              onClick={() => handlePeriodChange('current_year')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedPeriod === 'current_year'
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Este Ano
            </button>
            <button
              onClick={() => {
                if (window.confirm('⚠️ ATENÇÃO: Carregar todos os períodos pode ser lento se você tiver muitos registros. Deseja continuar?')) {
                  handlePeriodChange('all')
                }
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all border-2 ${
                selectedPeriod === 'all'
                  ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                  : 'bg-white text-orange-600 border-orange-600 hover:bg-orange-50'
              }`}
            >
              Todos os Períodos
            </button>
          </div>
        </div>
        {selectedPeriod !== 'all' && filterDateStart && filterDateEnd && (
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-2">
            <CalendarIcon className="w-4 h-4 text-blue-600" />
            <span>
              Mostrando registros de <strong>{new Date(filterDateStart).toLocaleDateString('pt-BR')}</strong> até <strong>{new Date(filterDateEnd).toLocaleDateString('pt-BR')}</strong>
            </span>
          </div>
        )}
        {selectedPeriod === 'all' && (
          <div className="mt-3 flex items-center gap-2 text-sm text-orange-700 bg-orange-50 border border-orange-300 rounded-lg p-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-orange-600" />
            <span>
              <strong>Modo "Todos os Períodos" ativo:</strong> Carregando até 500 registros mais recentes. Use filtros de data para melhor performance.
            </span>
          </div>
        )}
      </div>

      {/* Filter and Bills Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Contas a Pagar
              {filteredBills.length !== displayBills.length && (
                <span className="ml-2 text-sm text-gray-500">
                  ({filteredBills.length} de {displayBills.length})
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {/* Search input */}
              <input
                type="text"
                placeholder="Buscar por descrição ou documento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
              />
              {/* Status filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">Todas</option>
                <option value="open">Em Aberto</option>
                <option value="pending">Pendentes</option>
                <option value="overdue">Vencidas</option>
                <option value="partial">Parciais</option>
                <option value="paid">Pagas</option>
              </select>
              {/* Advanced filters toggle */}
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                {showAdvancedFilters ? '✕ Fechar Filtros' : '⚙️ Filtros Avançados'}
              </button>
            </div>
          </div>

          {/* Advanced filters panel */}
          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Date range */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Data Início
                  </label>
                  <input
                    type="date"
                    value={filterDateStart}
                    onChange={(e) => setFilterDateStart(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Data Fim
                  </label>
                  <input
                    type="date"
                    value={filterDateEnd}
                    onChange={(e) => setFilterDateEnd(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                {/* Category filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Categoria
                  </label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="all">Todas Categorias</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                  </select>
                </div>
                {/* Supplier filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Fornecedor
                  </label>
                  <select
                    value={filterSupplier}
                    onChange={(e) => setFilterSupplier(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="all">Todos Fornecedores</option>
                    {displaySuppliers.map(sup => (
                      <option key={sup.id} value={sup.id}>{sup.nome}</option>
                    ))}
                  </select>
                </div>
                {/* Amount min */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Valor Mínimo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={filterAmountMin}
                    onChange={(e) => setFilterAmountMin(e.target.value)}
                    placeholder="R$ 0,00"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                {/* Amount max */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Valor Máximo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={filterAmountMax}
                    onChange={(e) => setFilterAmountMax(e.target.value)}
                    placeholder="R$ 10.000,00"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              {/* Clear filters button */}
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => {
                    setSelectedPeriod('current_month')
                    const defaultRange = getDateRange('current_month')
                    setFilterDateStart(defaultRange.start)
                    setFilterDateEnd(defaultRange.end)
                    setFilterCategory('all')
                    setFilterSupplier('all')
                    setFilterAmountMin('')
                    setFilterAmountMax('')
                    setCurrentPage(1)
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedBills.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-blue-700 font-medium">
                {selectedBills.length} conta(s) selecionada(s)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleBulkMarkAsPaid}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Marcar como Pago
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Deseja realmente excluir ${selectedBills.length} conta(s)?`)) {
                      handleBulkDelete()
                    }
                  }}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Excluir
                </button>
                <button
                  onClick={() => setSelectedBills([])}
                  className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedBills.length === filteredBills.length && filteredBills.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBills(filteredBills.map(b => b.id))
                      } else {
                        setSelectedBills([])
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fornecedor
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('description')}
                >
                  <div className="flex items-center gap-1">
                    Descrição
                    {sortField === 'description' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center gap-1">
                    Valor
                    {sortField === 'amount' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('due_date')}
                >
                  <div className="flex items-center gap-1">
                    Vencimento
                    {sortField === 'due_date' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === 'status' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedBills.map((bill) => {
                const daysUntilDue = bill.is_overdue ? -bill.days_overdue : getDaysUntilDue(bill.due_date)
                return (
                  <tr key={bill.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedBills.includes(bill.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBills([...selectedBills, bill.id])
                          } else {
                            setSelectedBills(selectedBills.filter(id => id !== bill.id))
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <BuildingOfficeIcon className="w-5 h-5 text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {displaySuppliers.find(s => s.id === bill.supplier_id)?.nome || `Fornecedor ${bill.supplier_id}`}
                          </div>
                          <div className="text-sm text-gray-500">{bill.document_number}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{bill.description}</div>
                      <div className="text-sm text-gray-500">
                        {categories.find(c => c.id === bill.category_id)?.name || categories.find(c => c.id === bill.category_id)?.nome || `Categoria ${bill.category_id}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(parseFloat(bill.original_amount || bill.valor || '0'))}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(bill.due_date)}</div>
                      {bill.status === 'pending' && (
                        <div className={`text-sm ${daysUntilDue < 0 ? 'text-red-600' : daysUntilDue <= 7 ? 'text-yellow-600' : 'text-gray-500'}`}>
                          {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} dias em atraso` :
                           daysUntilDue === 0 ? 'Vence hoje' :
                           `${daysUntilDue} dias`}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(bill.status)}`}>
                        {getStatusLabel(bill.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {bill.status === 'pending' && (
                          <button
                            onClick={() => handlePayBill(bill)}
                            className="text-green-600 hover:text-green-900 text-sm"
                          >
                            Pagar
                          </button>
                        )}
                        <button
                          onClick={() => handleEditBill(bill)}
                          className="text-blue-600 hover:text-blue-900 text-sm"
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <span>Itens por página:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value))
                  setCurrentPage(1)
                }}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              <span className="ml-4">
                Mostrando {startIndex + 1}-{Math.min(endIndex, filteredBills.length)} de {filteredBills.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                ««
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                ‹
              </button>

              <span className="px-3 py-1 text-sm text-gray-700">
                Página {currentPage} de {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                ›
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                »»
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Suppliers Overview */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Fornecedores</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displaySuppliers.slice(0, 6).map((supplier) => (
            <div key={supplier.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{supplier.nome}</h4>
                <div className={`w-3 h-3 rounded-full ${supplier.status === 'ATIVO' ? 'bg-green-400' : 'bg-gray-300'}`}></div>
              </div>
              <div className="text-sm text-gray-600">
                Status: {supplier.status}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Nova Conta a Pagar</h3>
            <form className="space-y-4" onSubmit={handleCreateBill}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500">
                    <option value="payable">Conta a Pagar</option>
                    <option value="receivable">Conta a Receber</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número do Documento
                  </label>
                  <input
                    type="text"
                    value={formData.document_number}
                    onChange={(e) => setFormData({...formData, document_number: e.target.value})}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${formErrors.document_number ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                    placeholder="FAT-001"
                  />
                  {formErrors.document_number && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.document_number}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${formErrors.description ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                  placeholder="Descrição da conta"
                />
                {formErrors.description && (
                  <p className="text-red-600 text-xs mt-1">{formErrors.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Original *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.original_amount}
                    onChange={(e) => setFormData({...formData, original_amount: e.target.value})}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${formErrors.original_amount ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                    placeholder="0,00"
                  />
                  {formErrors.original_amount && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.original_amount}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fornecedor/Cliente *
                  </label>
                  <select
                    value={formData.supplier_id}
                    onChange={(e) => setFormData({...formData, supplier_id: e.target.value})}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${formErrors.supplier_id ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                  >
                    <option value="">Selecione um fornecedor</option>
                    {displaySuppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>{supplier.nome}</option>
                    ))}
                  </select>
                  {formErrors.supplier_id && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.supplier_id}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Selecione uma categoria (opcional)</option>
                  {categories
                    .filter((cat) => cat.type === 'expense') // Filtrar apenas categorias de despesa
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nome || cat.name}
                      </option>
                    ))
                  }
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {categories.filter((cat) => cat.type === 'expense').length > 0
                    ? `${categories.filter((cat) => cat.type === 'expense').length} categoria(s) de despesa disponível(is)`
                    : 'Nenhuma categoria de despesa cadastrada'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Emissão *
                  </label>
                  <input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${formErrors.issue_date ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                  />
                  {formErrors.issue_date && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.issue_date}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Vencimento *
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${formErrors.due_date ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'}`}
                  />
                  {formErrors.due_date && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.due_date}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                  placeholder="Observações adicionais..."
                ></textarea>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Criar Conta
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayModal && selectedBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Pagar Conta</h3>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Conta selecionada:</p>
              <p className="font-medium">{selectedBill.description}</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(parseFloat(selectedBill.original_amount || selectedBill.valor || '0'))}</p>
            </div>

            <form className="space-y-4" onSubmit={processPayment}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conta Bancária para Débito
                </label>
                <select
                  value={paymentData.account_id}
                  onChange={(e) => setPaymentData({...paymentData, account_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Selecione a conta bancária</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {formatCurrency(account.balance || 0)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forma de Pagamento
                </label>
                <select
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="debit_card">Débito</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="pix">PIX</option>
                  <option value="cash">Dinheiro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data do Pagamento
                </label>
                <input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações do Pagamento
                </label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                  placeholder="Observações sobre o pagamento..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Confirmar Pagamento
                </button>
                <button
                  type="button"
                  onClick={() => setShowPayModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Editar Conta a Pagar</h3>
            <form className="space-y-4" onSubmit={handleUpdateBill}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número do Documento
                  </label>
                  <input
                    type="text"
                    value={formData.document_number}
                    onChange={(e) => setFormData({...formData, document_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="FAT-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fornecedor
                  </label>
                  <select
                    value={formData.supplier_id}
                    onChange={(e) => setFormData({...formData, supplier_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione um fornecedor</option>
                    {displaySuppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>{supplier.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Descrição da conta"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Original
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.original_amount}
                    onChange={(e) => setFormData({...formData, original_amount: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Vencimento
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Observações adicionais..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Salvar Alterações
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default BillsPage